import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { subDays } from 'date-fns';
import OperationsAnalyticsCenter from './OperationsAnalyticsCenter';
import { MealAnalyticsSection } from '@/components/manager/MealAnalyticsSection';
import { AnalyticsSection } from '@/components/manager/AnalyticsSection';
import { useSites } from '@/hooks/useSites';
import { ReportButton } from '@/components/reports/ReportButton';


import type { Client, Alert } from '@/types/shelter';

interface AnalyticsHubProps {
  clients?: Client[];
  alerts?: Alert[];
  totalCapacity?: number;
  checkedInCount?: number;
  dailyRosterCount?: number;
  userName?: string;
  isDemo?: boolean;
}

export function AnalyticsHub({
  clients = [],
  alerts = [],
  totalCapacity = 0,
  checkedInCount = 0,
  dailyRosterCount = 0,
  userName = 'User',
  isDemo = false,
}: AnalyticsHubProps) {
  const [range, setRange] = useState<'7' | '30' | '90'>('30');
  const [siteId, setSiteId] = useState<string>('all');
  const { sites } = useSites();

  const days = parseInt(range, 10);
  const rangeStart = subDays(new Date(), days);
  const rangeEnd = new Date();
  const effectiveSite = siteId === 'all' ? null : siteId;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-semibold">Agency Analytics</h2>
          <p className="text-sm text-muted-foreground">Trends and performance across every facility — operations, services, and meals.</p>
        </div>
        <div className="flex gap-2">
          <ReportButton
            title="Agency Analytics Summary"
            rows={clients as any[]}
            columns={[
              { key: 'name', label: 'Client', value: (c: any) => `${c.firstName || c.first_name || ''} ${c.lastName || c.last_name || ''}`.trim() },
              { key: 'status', label: 'Status', value: (c: any) => c.status || '—' },
              { key: 'facility', label: 'Facility', value: (c: any) => sites.find(s => s.id === c.site_id)?.name || '—' },
              { key: 'room', label: 'Bed', value: (c: any) => c.roomNumber || c.room_number || '—' },
              { key: 'returnee', label: 'Returnee', value: (c: any) => (c.isReturnee ? 'Yes' : 'No') },
              { key: 'created', label: 'Intake date', value: (c: any) => (c.created_at ? new Date(c.created_at).toLocaleDateString() : '—') },
            ]}
            getDate={(c: any) => c.created_at}
            getSiteId={(c: any) => c.site_id ?? null}
            summary={(rows) => ({
              'Active clients': rows.length,
              'Currently bedded': checkedInCount,
              'Total capacity': totalCapacity,
              'Occupancy': totalCapacity > 0 ? `${Math.round((checkedInCount / totalCapacity) * 100)}%` : 'n/a',
              'On roster today': dailyRosterCount,
              'Active alerts': alerts.filter((a: any) => a.status === 'active').length,
            })}
          />

          <Select value={siteId} onValueChange={setSiteId}>
            <SelectTrigger className="w-44 h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Facilities</SelectItem>
              {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={range} onValueChange={(v: '7' | '30' | '90') => setRange(v)}>
            <SelectTrigger className="w-32 h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="operations" className="space-y-3">
        <TabsList>
          <TabsTrigger value="operations">Operations</TabsTrigger>
          <TabsTrigger value="meals">Meals & Beverages</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
        </TabsList>

        <TabsContent value="operations">
          <OperationsAnalyticsCenter
            clients={clients}
            alerts={alerts}
            totalCapacity={totalCapacity}
            checkedInCount={checkedInCount}
            dailyRosterCount={dailyRosterCount}
            userName={userName}
          />
        </TabsContent>

        <TabsContent value="meals">
          <MealAnalyticsSection rangeStart={rangeStart} rangeEnd={rangeEnd} siteId={effectiveSite} />
        </TabsContent>

        <TabsContent value="trends">
          <AnalyticsSection isDemo={isDemo} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default AnalyticsHub;
